import * as m from '@staiter/i18n';
import { Link, Navigate, createFileRoute } from '@tanstack/react-router';
import {
  CheckCircle2,
  FileClock,
  HardDrive,
  KeySquare,
  Plug2,
  ShieldAlert,
  TrendingUp,
  Users,
  Wallet,
  Workflow,
} from 'lucide-react';
import { useAuthStore } from '~/store/auth';
import styles from './_auth.admin.module.css';

/**
 * SET-AUDIT-8 (pass3 settings audit): /admin index overview вместо silent redirect.
 * Cards-grid с 10 admin areas — каждый показывает purpose + click → tab.
 * Future: добавить live health metrics (active integrations / pending approvals / etc).
 */
export const Route = createFileRoute('/_auth/admin/')({ component: AdminOverview });

type AdminCard = {
  to: string;
  title: () => string;
  description: () => string;
  Icon: typeof Plug2;
};

const CARDS: AdminCard[] = [
  {
    to: '/admin/integrations',
    title: m.admin_nav_integrations,
    description: m.admin_card_desc_integrations,
    Icon: Plug2,
  },
  {
    to: '/admin/sync-staging',
    title: m.admin_nav_sync_staging,
    description: m.admin_card_desc_sync_staging,
    Icon: Workflow,
  },
  {
    to: '/admin/approval-matrix',
    title: m.admin_nav_approval_matrix,
    description: m.admin_card_desc_approval_matrix,
    Icon: ShieldAlert,
  },
  {
    to: '/admin/users',
    title: m.admin_nav_users,
    description: m.admin_card_desc_users,
    Icon: Users,
  },
  {
    to: '/admin/warehouse-access',
    title: m.admin_nav_warehouse_access,
    description: m.admin_card_desc_warehouse_access,
    Icon: HardDrive,
  },
  {
    to: '/admin/bank-import',
    title: m.admin_nav_bank_import,
    description: m.admin_card_desc_bank_import,
    Icon: Wallet,
  },
  {
    to: '/admin/fx-rates',
    title: m.admin_nav_fx_rates,
    description: m.admin_card_desc_fx_rates,
    Icon: TrendingUp,
  },
  {
    to: '/admin/jobs',
    title: m.admin_nav_jobs,
    description: m.admin_card_desc_jobs,
    Icon: FileClock,
  },
  {
    to: '/admin/audit',
    title: m.admin_nav_audit,
    description: m.admin_card_desc_audit,
    Icon: KeySquare,
  },
  {
    to: '/admin/memory',
    title: m.admin_nav_memory,
    description: m.admin_card_desc_memory,
    Icon: CheckCircle2,
  },
];

function AdminOverview(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  // С-I8 Р.5 (В-59): руководителю компании из админки открыт реестр — вход сразу туда.
  if (role === 'company_admin') return <Navigate to="/admin/users" replace />;
  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 600, margin: '0 0 8px 0' }}>
        {m.ui_administrirovanie()}
      </h1>
      <p className={styles.muted} style={{ marginTop: 0, marginBottom: 24 }}>
        {m.ui_sistemnye_nastroyki_staiter_kazhdyy_razdel_dostupen_chere()}
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 12,
        }}
      >
        {CARDS.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            style={{
              display: 'block',
              padding: 16,
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              background: 'var(--color-surface)',
              textDecoration: 'none',
              color: 'inherit',
              transition: 'border-color 120ms ease, transform 120ms ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <c.Icon size={18} aria-hidden="true" />
              <strong style={{ fontSize: 15 }}>{c.title()}</strong>
            </div>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: 'var(--color-text-secondary)',
                lineHeight: 1.4,
              }}
            >
              {c.description()}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}
